import { Controller } from '@hotwired/stimulus'
import i18next from 'i18next'

import { debounce } from '../../utils/timeUtils'
import { TirinnovoDrawing } from './lib/tirinnovo_drawing'
import { ApiService } from '../../services/apiService'

// Connects to data-controller="draw--form"
export default class extends Controller {
  static values = {
    siteId: String,
    formId: String,
    tab: String,
    fromMenu: Boolean
  }
  static targets = [
    'showInModalOnly',
    'blueprintData', 'blueprintReferenceValue', 'drawBtn',
    'drawingId', 'drawingTitle', 'drawingType', 'drawingBlueprintId', 'drawingShapes', 'total', 'totalSelected',
    'sectionHeight', 'volume', 'perimeter', 'emptyState'
  ]

  blueprintDataTargetConnected() {
    this.blueprint = JSON.parse(this.blueprintDataTarget.value)
    if (!this.blueprint.configuration) {
      this.blueprint.configuration = {}
    }
    this.drawingBlueprintIdTarget.value = this.blueprint.id
  }

  drawingTitleTargetConnected() {
    this.launchDrawingBoard()
    this.loadBlueprint()
    let drawing = {
      id: this.drawingIdTarget.value,
      drawing_type: 'area',
      blueprint_id: null,
      title: null,
      shapes: JSON.parse(this.drawingShapesTarget.value),
      total: 0,
      section_height: this.sectionHeightTarget.value
    }
    this.drawingIdTarget.value = drawing.id || ''
    this.drawingShapesTarget.value = JSON.stringify(drawing.shapes)
    this.drawer.loadShapes(drawing.drawing_type, drawing.shapes)
    this.drawer.drawingType = drawing.drawing_type
    this.drawer.fitView()
    this.updateButtonsVisibility()
    this.updateEmptyState()
  }

  showInModalOnlyTargetConnected() {
    this.drawingModalEl = document.getElementById('drawingModal')
    if (this.drawingModalEl?.dataset?.formId) {
      this.showInModalOnlyTarget.classList.remove('d-none')
    }
  }

  connect() {
    this.debouncedUpdateRemoteBlueprint = debounce(this.updateRemoteBlueprint.bind(this), 100)
    this.isDrawing = false
    this.updateButtonsVisibility()
  }

  disconnect() {
    window.modal?.hide()
  }

  toggleDrawing() {
    this.drawer.onDrawBtnClick()
  }

  launchDrawingBoard() {
    this.drawer?.dispose()
    this.drawer = new TirinnovoDrawing({
      drawType: 'area',
      blueprint: this.blueprint,
      onTotalChange: this.onTotalChange.bind(this),
      onUpdateReferencePoints: debounce(this.onUpdateReferencePoints.bind(this), 100),
      onUpdatePoints: debounce(this.onUpdatePoints.bind(this), 100),
      initialShapes: this.drawingShapesTarget.value,
      drawBtn: this.drawBtnTarget,
      configuration: this.blueprint.configuration,
    })

    const originalHandleCanvasClick = this.drawer.handleCanvasClick
    this.drawer.handleCanvasClick = (event) => {
      if (event.target === this.drawer.canvas.upperCanvasEl) {
        // Clicked canvas - do nothing
      } else if (event.target === this.drawBtnTarget) {
        // Clicked draw button - handled by onDrawBtnClick
      } else {
        // Clicked outside canvas - do nothing
      }
    }
  }

  switchDrawingType(e) {
    this.drawer.switchDrawingType(e.target.value)
  }

  updateBackgroundColorChange(e) {
    this.drawer.updateBackgroundColor(e.target.value)
    this.blueprint.configuration['bg_color'] = e.target.value
    this.debouncedUpdateRemoteBlueprint()
  }

  updateBackgroundOpacityChange(e) {
    this.drawer.updateBackgroundOpacity(e.target.value)
    this.blueprint.configuration['opacity'] = e.target.value
    this.debouncedUpdateRemoteBlueprint()
  }

  updateGridOpacityChange(e) {
    this.drawer.updateGridOpacity(e.target.value)
    this.blueprint.configuration['grid_opacity'] = e.target.value
    this.debouncedUpdateRemoteBlueprint()
  }

  updateReferenceValue(e) {
    const value = e.target.value
    this.blueprint.reference_value = value
    this.drawer.updateReferenceValue(value)
    this.debouncedUpdateRemoteBlueprint()
  }

  onUpdateReferencePoints(points) {
    this.blueprint.reference_points = points
    this.updateRemoteBlueprint()
  }

  onUpdatePoints(shapes) {
    const updatedShapes = JSON.stringify(shapes)
    if (this.drawingShapesTarget.value != updatedShapes) {
      this.drawingShapesTarget.value = updatedShapes
      this.saveDrawing()
    }
    this.updateEmptyState()
  }

  onTotalChange({ total, totalSelected, perimeter }) {
    const output = {
      total: (total || 0).toFixed(2),
      totalSelected: (totalSelected || 0).toFixed(2),
      perimeter: (perimeter || 0).toFixed(2)
    }
    this.totalTarget.value = output.total
    this.totalSelectedTarget.innerHTML = output.totalSelected > 0 ? output.totalSelected : ''
    this.perimeterTarget.textContent = output.perimeter
    this.calculateVolume()
  }

  resetViewClick() {
    this.drawer.fitView()
  }

  zoomAllClick() {
    this.drawer.fitAll()
  }

  updateEmptyState() {
    if (!this.hasEmptyStateTarget) return

    const hasShapes = JSON.parse(this.drawingShapesTarget.value || '[]').length > 0
    this.emptyStateTarget.classList.toggle('d-none', hasShapes || Boolean(this.blueprint?.file_url))
  }

  async onUploadBlueprintFile(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("authenticity_token", document.querySelector('meta[name=csrf-token]').content);
    formData.append("blueprint[file]", file)

    fetch(`/sites/${this.siteIdValue}/blueprints`, {
      method: 'POST',
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      body: formData,
    })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
        const jsonBlueprint = (new DOMParser()).parseFromString(html, "text/xml").querySelector('[data-draw--form-target="blueprintData"]').getAttribute('value')
        this.blueprint = JSON.parse(jsonBlueprint)
        this.loadBlueprint()
        this.updateEmptyState()
      })
  }

  async loadBlueprint() {
    if (!this.blueprint.id || !this.blueprint.file_url) {
      this.drawer.resize()
      this.drawer.drawGrid()
      return
    }

    document.getElementById('loading-overlay').classList.remove('d-none')
    const fileResponse = await fetch(this.blueprint.file_url)
    document.getElementById('loading-overlay').classList.add('d-none')
    this.blueprintReferenceValueTarget.value = this.blueprint.reference_value

    this.blueprintFile = await fileResponse.blob()
    this.drawer.loadBlueprint({
      file: this.blueprintFile,
      reference_points: this.blueprint.reference_points,
      reference_value: this.blueprint.reference_value,
    })
  }

  async updateRemoteBlueprint() {
    if (!this.blueprint.id) { return }
    await ApiService.patch(`/sites/${this.siteIdValue}/blueprints/${this.blueprint.id}`, { blueprint: this.blueprint })
  }

  async editBlueprintClick(event) {
    const currentTitle = event.target.closest('a').dataset.title
    const title = prompt(i18next.t('controllers__draw__form_controller.new_reference_image_name'), currentTitle)
    if (title) {
      const blueprintId = event.currentTarget.dataset.blueprint_id
      const formData = new FormData();
      formData.append('authenticity_token', document.querySelector('meta[name=csrf-token]').content);
      formData.append('blueprint[title]', title);

      fetch(`/sites/${this.siteIdValue}/blueprints/${blueprintId}`, {
        method: 'PATCH',
        headers: { Accept: 'text/vnd.turbo-stream.html' },
        body: formData,
      })
        .then(response => response.text())
        .then(html => Turbo.renderStreamMessage(html))
    }
  }

  async saveDrawing() {
    const rawFormData = Object.fromEntries(new FormData(document.getElementById('drawing_form')))
    const formData = new FormData()
    formData.append('authenticity_token', document.querySelector('meta[name=csrf-token]').content)
    formData.append('drawing[blueprint_id]', rawFormData['drawing[blueprint_id]'])
    formData.append('drawing[shapes]', rawFormData['drawing[shapes]'])
    formData.append('drawing[drawing_type]', rawFormData['drawing[drawing_type]'])
    formData.append('drawing[total]', rawFormData['drawing[total]'])
    formData.append('drawing[section_height]', this.sectionHeightTarget.value)

    const drawingId = rawFormData['drawing[id]']
    const url = drawingId ? `/sites/${this.siteIdValue}/drawings/${drawingId}` : `/sites/${this.siteIdValue}/drawings`
    fetch(url, {
      method: drawingId ? 'PATCH' : 'POST',
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      body: formData,
    })
      .then(response => response.text())
      .then(html => {
        if (!drawingId) {
          Turbo.renderStreamMessage(html)
        }
      })
  }

  async editDrawingClick(event) {
    const title = prompt(i18next.t('controllers__draw__form_controller:drawing_name_prompt'), this.drawingTitleTarget.innerHTML)
    if (title) {
      const drawingId = event.currentTarget.dataset.drawing_id
      const formData = new FormData();
      formData.append('authenticity_token', document.querySelector('meta[name=csrf-token]').content);
      formData.append('drawing[title]', title);
      formData.append('drawing[id]', drawingId);

      fetch(`/sites/${this.siteIdValue}/drawings`, {
        method: 'POST',
        headers: { Accept: "text/vnd.turbo-stream.html" },
        body: formData,
      })
        .then(response => response.text())
        .then(html => Turbo.renderStreamMessage(html))
    }
  }

  insertTotal(event) {
    const unit = event.params.unit // eg. 'm3'
    const unitKind = event.params.unitKind // area|volume|perimeter
    const detail = {
      unit,
      unitKind,
      total: unitKind === 'area' ? this.totalTarget.value : this[`${unitKind}Target`].textContent,
      uniqueId: this.formIdValue,
      drawingId: this.drawingIdTarget.value,
      drawingTitle: this.drawingTitleTarget.innerHTML,
    }

    if (!(detail.total > 0)) {
      alert(i18next.t(`controllers__draw__form_controller:missing_${unitKind}`))
      return false
    }

    document.getElementById('drawingModal').remove()

    // Warning! When changing this please also look at '_form_manual_measurements.html.erb' view
    document.querySelector(`#site_task_measurement_${this.formIdValue}`)?.dispatchEvent(
      new CustomEvent('updateDrawing', { detail })
    )
  }

  cloneDrawingClick(event) {
    const defaultTitle = event.currentTarget.dataset.defaultTitle
    const title = prompt(i18next.t('controllers__draw__form_controller:e'), defaultTitle)
    if (title) {
      const formData = new FormData();
      formData.append('drawing[title]', title);
      fetch(event.currentTarget.href, {
        method: 'POST',
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content
        },
        body: formData
      })
        .then(response => response.text())
        .then(html => Turbo.renderStreamMessage(html))
    }
  }

  updateSectionHeight(e) {
    const value = e.target.value
    this.sectionHeightTarget.value = value
    this.calculateVolume()
    this.saveDrawing()
  }

  calculateVolume() {
    const area = parseFloat(this.totalTarget.value) || 0
    const height = parseFloat(this.sectionHeightTarget.value) || 0
    const volume = area * height
    this.volumeTarget.textContent = volume.toFixed(2)
  }

  updateButtonsVisibility() {
    this.showInModalOnlyTargets.forEach(button => {
      if (this.fromMenuValue) {
        button.classList.add('d-none')
        return
      }

      if (this.formIdValue) {
        button.classList.remove('d-none')
      } else {
        button.classList.add('d-none')
      }
    })
  }

  formIdValueChanged() {
    this.updateButtonsVisibility()
  }

  fromMenuValueChanged() {
    this.updateButtonsVisibility()
  }

  drawingTitleTargetDisconnected() {
    this.updateButtonsVisibility()
  }
}
